import classNames from 'classnames';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Switch } from 'antd';
import { MButton, MAvatar } from '@Components/Base';

import { useAppSelector, useLogout } from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { useGetEmpPhotoQuery } from '@Store/api/dictionaryApiSlice';

import { ReactComponent as NotifyBellIcon } from '@Icons/additional/notification-alt.svg';
import { ReactComponent as DarkModeMoonIcon } from '@Icons/additional/profile/dark-mode-moon.svg';
import { ReactComponent as LangIcon } from '@Icons/additional/profile/translate.svg';
import { ReactComponent as InfoHelpIcon } from '@Icons/additional/profile/info-help.svg';
import { ReactComponent as TermsIcon } from '@Icons/additional/profile/terms-document.svg';
import { ReactComponent as PrivacyIcon } from '@Icons/additional/profile/privacy-document.svg';
import { ReactComponent as LogoutIcon } from '@Icons/additional/profile/exit.svg';
import { ReactComponent as ChevroneRightIcon } from '@Icons/additional/profile/chevrone-black-right.svg';
import { ReactComponent as NextArrowIcon } from '@Icons/additional/arrow-left.svg';

import styles from './profile-page.module.css';

const MobileProfilePage: React.FC = () => {
  const currentUser = useAppSelector(currentUserSelector);
  const { data: photoSrc } = useGetEmpPhotoQuery(
    currentUser.empImageName || ''
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useLogout();

  const handleGoToDetails = (): void => {
    navigate('details');
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  const handleLogout = (): void => {
    logout();
  };

  const isOutlet = pathname.includes('details');

  if (isOutlet) {
    return <Outlet />;
  }

  return (
    <div className={styles.profilePage}>
      <div
        className={classNames(
          'trojan-neumorphism-box trojan-neumorphism-box-shadow',
          styles.backSection
        )}
        onClick={handleBack}
      >
        <NextArrowIcon />
      </div>
      <div
        className={classNames(
          'trojan-glassphormism-box trojan-glassphormism-box-shadow ',
          styles.detailsSection
        )}
        onClick={handleGoToDetails}
      >
        <MAvatar className={styles.avatar} size={88} src={photoSrc} />
        <div className={styles.userInfo}>
          <div className={styles.employee}>
            <div className={styles.name}>{currentUser?.empName}</div>
            <ChevroneRightIcon className={styles.icon} />
          </div>

          <div>{currentUser?.occupation}</div>
        </div>
      </div>
      <div className={styles.infoSection}>
        <div className={styles.title}>Preferences</div>
        <div className={styles.item}>
          <div className={styles.desc}>
            <NotifyBellIcon className={styles.icon} />
            Notifications
          </div>
          <div>
            <Switch defaultChecked />
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.desc}>
            <DarkModeMoonIcon className={styles.icon} />
            Dark Mode
          </div>
          <div>
            <Switch defaultChecked />
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.desc}>
            <LangIcon className={styles.icon} />
            Language
          </div>
          <ChevroneRightIcon className={styles.icon} />
        </div>
      </div>
      <div className={classNames(styles.infoSection, styles.flexGrow)}>
        <div className={styles.title}>General</div>
        <div className={styles.item}>
          <div className={styles.desc}>
            <InfoHelpIcon className={styles.icon} />
            Help
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.desc}>
            <TermsIcon className={styles.icon} />
            Terms & Conditions
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.desc}>
            <PrivacyIcon className={styles.icon} />
            Privacy Policy
          </div>
        </div>
      </div>
      <div className={styles.logout}>
        <MButton type="secondary" onClick={handleLogout}>
          <LogoutIcon />
          Logout
        </MButton>
      </div>
    </div>
  );
};

export default MobileProfilePage;
